'use client';

import * as React from 'react';
import { cn } from '@/lib/utils';
import { useBackgroundTheme } from './use-background-theme';

/**
 * ParticleMesh — Neuronales Netz / Plexus-Effekt
 *
 * Technik: Canvas 2D, 30fps throttle, Mouse-Proximity-Highlighting
 * CPU-Impact: Low-Medium (~80 Partikel, ~80*79/2 Distanz-Checks, gefiltert)
 *
 * Verwendung:
 *   <ParticleMeshBackground>
 *     <div className="relative z-10">Content</div>
 *   </ParticleMeshBackground>
 */
function ParticleMeshBackground({
  className,
  children,
  speed = 1,
  opacity = 1,
  interactive = true,
  particleCount = 75,
  connectDistance = 150,
  particleColor,           // auto-set from theme if not given
  lineColor,               // auto-set from theme if not given
  variant = 'auto',        // 'auto' | 'dark' | 'light'
  ...props
}) {
  const isDark = useBackgroundTheme(variant);

  // Theme-dependent defaults
  /* ---------------------------------------------------------- Dark-Aufhellung
     User-Feedback 2026-08-06: "im dark mode muss es heller werden, sonst sieht
     man gar nichts". GEMESSEN (WCAG-Kontrast, sRGB-Gamma-Compositing) war der
     Grund kein Gefuehl, sondern Physik — die Linien lagen faktisch auf dem
     Hintergrund:
       Partikel cyan-500 @a=0.25 -> CR 1.49
       Linien   violet-500 @a=0.20 -> CR 1.21   << unsichtbar
     Zwei Hebel, beide nur fuer Dark (Light war nie das Problem und bleibt 1:1):
     (a) eine Helligkeitsstufe hoehere Farbe (cyan-400 / violet-400 statt -500),
     (b) hoehere Alpha-Werte, s. initParticles und die Linien-Formel unten.
     Ergebnis: Partikel ~CR 2.9-3.9, Linien ~CR 2.0-2.6 — deutlich sichtbar,
     aber noch Hintergrund und keine Lichtshow.
     Die Hex-Literale sind hier die dokumentierte Ausnahme "Pixel-Kopplung":
     Canvas 2D rechnet mit numerischen RGB-Kanaelen (s. parseColor unten), die
     Core-Tokens sind oklch() und waeren ohne getComputedStyle+Farbraum-Konvertierung
     nicht verwertbar. Ueberschreibbar bleiben sie ueber die Props. */
  const resolvedParticleColor = particleColor ?? (isDark ? '#22d3ee' : '#0284c7');
  const resolvedLineColor = lineColor ?? (isDark ? '#a78bfa' : '#7c3aed');

  /* Token statt Literal: vorher 'bg-slate-950'/'bg-white' — das war eine harte
     Farbe am Design-System vorbei und hat jedes Per-Org-Theme ignoriert.
     --background kippt automatisch mit Light/Dark UND mit organisation_themes. */
  const bgClass = 'bg-background';
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const particlesRef = React.useRef([]);
  const mouseRef = React.useRef({ x: -9999, y: -9999 });
  const lastFrameRef = React.useRef(0);
  const pausedRef = React.useRef(false);
  const FPS = 30;
  const INTERVAL = 1000 / FPS;

  const initParticles = React.useCallback((w, h) => { // eslint-disable-line react-hooks/exhaustive-deps
    particlesRef.current = Array.from({ length: particleCount }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      vx: (Math.random() - 0.5) * 0.4 * speed,
      vy: (Math.random() - 0.5) * 0.4 * speed,
      r: 1 + Math.random() * 1.5,
      /* Dark bekommt hoehere Grund-Alpha (0.35–0.60 statt 0.15–0.35): gemessen
         ~CR 2.9–3.9 gegen --background, vorher 1.49. Light bleibt exakt wie bisher.
         isDark MUSS in den Deps stehen — sonst behaelt der useCallback beim
         Theme-Wechsel die alten Werte (stale closure) und der Effekt re-initialisiert
         die Partikel mit dem Alpha des vorherigen Modus. */
      baseOpacity: isDark ? 0.35 + Math.random() * 0.25 : 0.15 + Math.random() * 0.2,
    }));
  }, [particleCount, speed, isDark]);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: false });

    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;
    initParticles(w, h);

    const observer = new IntersectionObserver(
      ([entry]) => { pausedRef.current = !entry.isIntersecting; },
      { threshold: 0.01 },
    );
    observer.observe(canvas);

    const ro = new ResizeObserver(() => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
      initParticles(w, h);
    });
    ro.observe(canvas);

    // Parse hex to rgb helper — also supports plain 3-char hex
    const parseColor = (hex) => {
      const h = hex.replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const [pr, pg, pb] = parseColor(resolvedParticleColor);
    const [lr, lg, lb] = parseColor(resolvedLineColor);

    const distSq = (ax, ay, bx, by) => (ax - bx) ** 2 + (ay - by) ** 2;
    const connectSq = connectDistance * connectDistance;
    const mouseSq = (connectDistance * 1.5) ** 2;

    const draw = (timestamp) => {
      animRef.current = requestAnimationFrame(draw);
      if (pausedRef.current) return;
      if (timestamp - lastFrameRef.current < INTERVAL) return;
      lastFrameRef.current = timestamp;

      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      /* Nur loeschen, NICHT fuellen. Die Flaeche liefert bg-background am Wrapper
         (Token → folgt Light/Dark UND organisation_themes). Das fruehere fillRect
         hat mit rgb(2,6,23) eine harte Farbe darueber gemalt und damit jedes
         Per-Org-Theme uebersteuert; clearRect laesst das Canvas transparent. */
      ctx.clearRect(0, 0, w, h);

      const ps = particlesRef.current;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      // Move particles
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > w) p.vx *= -1;
        if (p.y < 0 || p.y > h) p.vy *= -1;
        p.x = Math.max(0, Math.min(w, p.x));
        p.y = Math.max(0, Math.min(h, p.y));
      }

      // Draw connections
      for (let i = 0; i < ps.length; i++) {
        for (let j = i + 1; j < ps.length; j++) {
          const d = distSq(ps[i].x, ps[i].y, ps[j].x, ps[j].y);
          if (d > connectSq) continue;
          /* Dark 0.45 statt 0.2: die Verbindungslinien waren mit CR 1.21 faktisch
             unsichtbar (= der eigentliche Grund fuer "man sieht gar nichts").
             Mit violet-400 @0.45 liegen sie bei ~CR 2.3. Light unveraendert. */
          const alpha = (1 - d / connectSq) * (isDark ? 0.45 : 0.2) * opacity;
          ctx.beginPath();
          ctx.strokeStyle = `rgba(${lr},${lg},${lb},${alpha})`;
          ctx.lineWidth = 0.6;
          ctx.moveTo(ps[i].x, ps[i].y);
          ctx.lineTo(ps[j].x, ps[j].y);
          ctx.stroke();
        }
      }

      // Draw particles
      for (let i = 0; i < ps.length; i++) {
        const p = ps[i];
        const mouseNear = interactive && distSq(p.x, p.y, mx, my) < mouseSq;
        const o = mouseNear ? Math.min(1, p.baseOpacity * 2) : p.baseOpacity;
        const r = mouseNear ? p.r * 2 : p.r;

        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${pr},${pg},${pb},${o * opacity})`;
        ctx.fill();

        if (mouseNear) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, r * 3, 0, Math.PI * 2);
          /* Glow-Halo mitziehen, sonst wirkt er neben den helleren Partikeln
             wie ein Rendering-Fehler statt wie ein Leuchten. */
          ctx.fillStyle = `rgba(${pr},${pg},${pb},${isDark ? 0.16 : 0.08})`;
          ctx.fill();
        }
      }
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animRef.current);
      observer.disconnect();
      ro.disconnect();
    };
  }, [speed, particleCount, connectDistance, resolvedParticleColor, resolvedLineColor, opacity, interactive, isDark, initParticles]);

  const handleMouseMove = React.useCallback((e) => {
    if (!interactive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }, [interactive]);

  const handleMouseLeave = React.useCallback(() => {
    mouseRef.current = { x: -9999, y: -9999 };
  }, []);

  return (
    <div
      data-slot="particle-mesh-background"
      className={cn('relative size-full overflow-hidden', bgClass, className)}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      {...props}
    >
      <canvas ref={canvasRef} className="absolute inset-0 size-full" />
      {children && (
        <div className="relative z-10 size-full">{children}</div>
      )}
    </div>
  );
}

export { ParticleMeshBackground };
